import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, inr } from "@/api";
import { PageHeader, EmptyState } from "@/components/Shared";
import { DynamicFields } from "@/components/DynamicFields";
import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Trash2, Package, AlertTriangle } from "lucide-react";

const EMPTY = {
  name: "", sku: "", hsn: "", unit: "Nos", gst_rate: 18, stock: 0, reorder_level: 0, purchase_cost: 0,
  prices: { retail: 0, shopkeeper: 0, dealer: 0, distributor: 0 },
};

export default function Products() {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [custom, setCustom] = useState({});
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null);

  const openNew = () => { setEditing(null); setForm(EMPTY); setCustom({}); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p.product_id);
    setForm({ name: p.name, sku: p.sku || "", hsn: p.hsn || "", unit: p.unit || "Nos", gst_rate: p.gst_rate, stock: p.stock, reorder_level: p.reorder_level, purchase_cost: p.purchase_cost || 0, prices: { ...p.prices } });
    setCustom(p.custom || {});
    setOpen(true);
  };

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        gst_rate: Number(form.gst_rate), stock: Number(form.stock), reorder_level: Number(form.reorder_level),
        purchase_cost: Number(form.purchase_cost || 0),
        prices: Object.fromEntries(Object.entries(form.prices).map(([k, v]) => [k, Number(v)])),
        custom,
      };
      if (editing) {
        await api.put(`/products/${editing}`, payload);
        toast.success("Product updated");
      } else {
        await api.post("/products", payload);
        toast.success("Product added with 4-tier price slab");
      }
      setOpen(false); setForm(EMPTY); setCustom({}); setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save product");
    } finally { setBusy(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete product ${p.name}?`)) return;
    try {
      await api.delete(`/products/${p.product_id}`);
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  const low = (p) => Number(p.stock) <= Number(p.reorder_level);

  return (
    <div data-testid="products-page">
      <PageHeader title="Products & Stock" sub="Base pricing per tier, GST rate, live stock and reorder levels">
        <Button onClick={openNew} data-testid="add-product-btn"
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold rounded-full transition-colors duration-200">
          <Plus className="w-4 h-4 mr-1" /> Add Product
        </Button>
      </PageHeader>

      {products.length === 0 ? (
        <EmptyState icon={Package} title="No products yet" sub="Add products with their 4-tier slab prices" />
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="products-table">
            <thead>
              <tr className="text-left text-xs font-mono2 uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <th className="px-5 py-3">Product</th><th className="px-5 py-3">HSN</th>
                <th className="px-5 py-3">GST</th>
                <th className="px-5 py-3 text-right">Retail</th><th className="px-5 py-3 text-right">Shopkeeper</th>
                <th className="px-5 py-3 text-right">Dealer</th><th className="px-5 py-3 text-right">Distributor</th>
                <th className="px-5 py-3 text-right">Stock</th>
                {user?.role === "admin" && <th className="px-5 py-3 text-right">Actions</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {products.map((p) => (
                <tr key={p.product_id} className="hover:bg-slate-50 transition-colors duration-150" data-testid={`product-row-${p.product_id}`}>
                  <td className="px-5 py-3.5">
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="text-xs text-slate-500 font-mono2">{p.sku || "—"} · {p.unit}</p>
                  </td>
                  <td className="px-5 py-3.5 font-mono2 text-slate-600">{p.hsn || "—"}</td>
                  <td className="px-5 py-3.5 text-slate-700">{p.gst_rate}%</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-slate-800">{inr(p.prices?.retail)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-emerald-600">{inr(p.prices?.shopkeeper)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-blue-600">{inr(p.prices?.dealer)}</td>
                  <td className="px-5 py-3.5 text-right font-mono2 text-lime-600">{inr(p.prices?.distributor)}</td>
                  <td className="px-5 py-3.5 text-right">
                    <span className={`font-mono2 font-bold ${low(p) ? "text-red-600" : "text-slate-900"}`} data-testid={`product-stock-${p.product_id}`}>
                      {p.stock}
                    </span>
                    {low(p) && <AlertTriangle className="w-3.5 h-3.5 inline ml-1.5 text-red-600" />}
                  </td>
                  {user?.role === "admin" && (
                    <td className="px-5 py-3.5 text-right space-x-1">
                      <button onClick={() => openEdit(p)} data-testid={`product-edit-${p.product_id}`}
                        className="inline-flex items-center gap-1 text-xs text-slate-700 hover:text-emerald-700 px-2 py-1 rounded-lg hover:bg-slate-700/50 transition-colors duration-150">
                        Edit
                      </button>
                      <button onClick={() => remove(p)} data-testid={`product-delete-${p.product_id}`}
                        className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 px-2 py-1 rounded-lg hover:bg-red-500/10 transition-colors duration-150">
                        <Trash2 className="w-3.5 h-3.5" /> Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 text-slate-900 max-w-2xl" data-testid="add-product-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Product" : "Add Product"}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-2">
            <div className="col-span-2 space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Product Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="product-name-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            {[["SKU / Code", "sku"], ["HSN Code", "hsn"], ["Unit", "unit"]].map(([l, k]) => (
              <div key={k} className="space-y-1.5">
                <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`product-${k}-input`} className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
              </div>
            ))}
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">GST Slab %</Label>
              <select value={form.gst_rate} onChange={(e) => setForm({ ...form, gst_rate: e.target.value })} data-testid="product-gst-input"
                className="w-full h-10 rounded-lg border border-slate-300 bg-slate-50/80 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none cursor-pointer px-3 text-sm text-slate-900">
                {[0, 5, 12, 18, 28].map((r) => <option key={r} value={r}>{r}% GST</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Opening Stock</Label>
              <Input type="number" step="any" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Reorder Level (low-stock alert)</Label>
              <Input type="number" step="any" value={form.reorder_level} onChange={(e) => setForm({ ...form, reorder_level: e.target.value })} data-testid="product-reorder-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">Production / Purchase Cost (₹)</Label>
              <Input type="number" step="0.01" value={form.purchase_cost} onChange={(e) => setForm({ ...form, purchase_cost: e.target.value })} data-testid="product-cost-input" className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
            </div>
            <div className="col-span-2 rounded-xl border border-emerald-500/25 bg-emerald-500/5 p-4">
              <p className="text-xs font-mono2 uppercase tracking-[0.18em] text-emerald-600 mb-3">Category Price Slabs (₹ per unit)</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[["retail", "Retail (B2C)"], ["shopkeeper", "Shopkeeper"], ["dealer", "Dealer"], ["distributor", "Distributor"]].map(([k, l]) => (
                  <div key={k} className="space-y-1.5">
                    <Label className="text-xs font-semibold text-slate-800 uppercase tracking-wider">{l}</Label>
                    <Input type="number" step="0.01" required value={form.prices[k]}
                      onChange={(e) => setForm({ ...form, prices: { ...form.prices, [k]: e.target.value } })}
                      data-testid={`product-price-${k}-input`} className="bg-slate-50/80 border-slate-300 placeholder:text-slate-400 focus:bg-white focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 hover:border-slate-400 transition-all duration-150 outline-none text-slate-900 font-mono2" />
                  </div>
                ))}
              </div>
            </div>
            <DynamicFields entity="products" values={custom} onChange={(k, v) => setCustom({ ...custom, [k]: v })} />
            <div className="col-span-2 pt-2">
              <Button type="submit" disabled={busy} data-testid="product-save-btn"
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors duration-200">
                {busy ? "Saving…" : "Save Product"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
